import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

const Ctx = createContext(null);

export function useAuth() { return useContext(Ctx); }
export { Ctx };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('dlp_token');
    if (!token) { setLoading(false); return; }
    api.get('/api/auth/me').then(d => {
      setUser(d.user || d);
      wasAuthenticated.current = true;
      setLoading(false);
    }).catch(() => {
      localStorage.removeItem('dlp_token');
      setLoading(false);
      // Don't set sessionExpired here — this is just a stale token on load
    });
  }, []);

  // Listen for 401 from API layer — only show "expired" if user was actively logged in
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('dlp_token');
      if (wasAuthenticated.current) {
        setSessionExpired(true);
      }
      setUser(null);
      wasAuthenticated.current = false;
    };
    window.addEventListener('auth-expired', handler);
    return () => window.removeEventListener('auth-expired', handler);
  }, []);

  const login = async (email, password) => {
    const d = await api.post('/api/auth/login', { email, password });
    if (d.token) {
      localStorage.setItem('dlp_token', d.token);
      setUser(d.user);
      setSessionExpired(false);
      wasAuthenticated.current = true;
    }
    return d;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('dlp_token');
    setUser(null);
    wasAuthenticated.current = false;
  }, []);

  return (
    <Ctx.Provider value={{ user, setUser, login, logout, loading, sessionExpired, setSessionExpired }}>
      {children}
    </Ctx.Provider>
  );
}
